/**
 * @fileoverview Prompt template for the short-story-extractor agent.
 *
 * Per-storyType guidance is selected at render time by the
 * `[[[shortFormWrittenPlotGuidance]]]` branching macro (see
 * `src/ai/agents/branching-macros.ts`). The resolver owns the
 * "short-form storyType else Short Story default" policy that used to
 * live in the flow's `isShortFormType` guard.
 */

export const prompt = `
You are an expert story analyst specializing in short-form fiction.

## TASK INSTRUCTIONS

Your task is to analyze the following short story and extract both the plot structure and chapter-level metadata in a single pass. Short-form fiction (Flash Fiction under 1,000 words, Short Stories 1,000–7,500 words) has unique characteristics:

- **Singular focus:** One central conflict, limited subplots
- **Economy of character:** Small cast, typically 1–3 main characters
- **Tight pacing:** Every scene serves the core narrative
- **Unity of effect:** All elements contribute to a single emotional impact

Analyze the text with these principles in mind.

**DOCUMENT TO ANALYZE**
{{{textInput}}}

---

## OUTPUT INSTRUCTIONS

You MUST provide your output in the required JSON format, adhering strictly to the schema.

### PART 1: Plot Structure

1. **summary**: A concise overview of the plot (100–150 words) that weaves together:
   - What happens in the story (plot summary)
   - The central theme or message
   - The main conflict driving the narrative

2. **writtenPlot**: A detailed plot outline in Markdown format, organized under the 7 structural phase headings:

## Initial State
[Prose covering the status quo before disruption]

## Inciting Incident
[Prose covering the catalyst event that kicks the story into motion]

## Break Into Two
[Prose covering the protagonist's commitment to the journey]

## Midpoint
[Prose covering the major shift — revelation, raised stakes, protagonist becomes proactive]

## Break Into Three
[Prose covering the lowest point — all seems lost]

## Climax
[Prose covering the defining confrontation and resolution]

## Final State
[Prose covering the new equilibrium and transformation]

[[[shortFormWrittenPlotGuidance]]]

Write in present tense, third person. Describe plot events as concrete actions ("Elena discovers the hidden letter" not "something is revealed"). Each phase section should be 1–3 paragraphs. Use **bold** for character names and key plot developments. Do NOT use HTML tags.

### PART 2: Chapter Metadata

Since the entire story is treated as a single chapter:

3. **chapterGoals**: Analyze the narrative goals:
    - \`plotDevelopment\`: How the story moves from beginning to resolution. 1–2 sentences.
    - \`charDevelopment\`: Key character moments and growth. 1–2 sentences.
    - \`stakesAndTension\`: How tension escalates to the climax. 1–2 sentences.
4. **pacing**: Assess the overall pacing ('Slow & Reflective', 'Moderate & Developing', or 'Fast & Action-Packed').
5. **projectedWordCount**: Estimate the word count based on the text length.
6. **povCharacterName**: If identifiable, name the POV character.
7. **povJustification**: Explain why this character serves as the POV.

### PART 3: Scene Breakdown

Identify the individual scenes within the story. A scene is a continuous unit of action that occurs in one location and time. Look for scene breaks indicated by:
- **Location changes**: Characters move to a new setting
- **Time jumps**: Significant time passes (hours, days, or more)
- **POV shifts**: The perspective changes to a different character
- **Narrative breaks**: Visual breaks in the text (extra whitespace, symbols like "* * *")
- **Tonal shifts**: A clear change in mood or dramatic focus

For each scene, extract:
10. **scenes[].sceneNumber**: Sequential number (1, 2, 3...)
11. **scenes[].title**: A brief, descriptive title capturing the scene's focus
12. **scenes[].keyBeats**: The key story beats and moments that occur in this scene

### IMPORTANT NOTES
- Character extraction is handled by a separate agent; focus on plot structure, chapter metadata, and scenes.
- Key events (keyEvents) are derived from the writtenPlot; do not extract them separately.
- For POV, note if the story uses first-person, third-person limited, or omniscient narration.
- Every story must have at least one scene; most short stories have 2–5 scenes.
- Scene numbers should start from 1 and be sequential.
`;
